<template>
  <div class="mp-page">
    <header class="mp-hero">
      <div class="mp-hero-title">
        MY PAYROLL
        <span class="mp-count-pill">{{ filteredPaid.length + filteredSuspended.length }}</span>
      </div>
      <div class="mp-hero-sub">
        Your paid payroll receipts and current suspended commissions.
      </div>
    </header>

    <!-- ================= FILTERS ================= -->
    <div class="mp-filterstrip">
      <div class="d-flex flex-wrap align-items-end" style="gap: 15px;">
        <div style="width: 260px;">
          <label class="mp-label">Date Range</label>
          <date-picker
            v-model="dateRange"
            range
            format="MM-DD-YYYY"
            valueType="format"
            :clearable="true"
            :editable="false"
            placeholder="All dates"
            style="width: 100%;"
          ></date-picker>
        </div>

        <div style="display: flex; gap: 6px; margin-bottom: 2px;">
          <b-button size="sm" variant="outline-secondary" @click="setDate('this_week')">This Week</b-button>
          <b-button size="sm" variant="outline-secondary" @click="setDate('last_week')">Last Week</b-button>
          <b-button size="sm" variant="outline-secondary" @click="setDate('this_month')">This Month</b-button>
          <b-button size="sm" variant="outline-secondary" @click="setDate('last_month')">Last Month</b-button>
          <b-button size="sm" variant="outline-secondary" @click="clearDate">All Time</b-button>
        </div>

        <div style="width: 220px;">
          <label class="mp-label">Location</label>
          <b-form-select
            v-model="selectedLocation"
            :options="locationOptions"
            size="sm"
          >
            <template #first>
              <b-form-select-option :value="null">All Locations</b-form-select-option>
            </template>
          </b-form-select>
        </div>

        <div
          v-if="canViewOthers"
          style="width: 220px;"
        >
          <label class="mp-label">Salesperson</label>
          <b-form-select
            v-model="viewAsSalesperson"
            :options="salespersonOptions"
            size="sm"
          >
            <template #first>
              <b-form-select-option :value="null">Myself ({{ myUsername }})</b-form-select-option>
            </template>
          </b-form-select>
        </div>

        <div class="ml-auto">
          <b-button
            class="mp-btn-primary"
            size="sm"
            @click="refreshAll"
            :disabled="loadingPaid || loadingSuspended"
          >
            <b-spinner small v-if="loadingPaid || loadingSuspended"></b-spinner>
            <b-icon v-else icon="arrow-clockwise"></b-icon>
            Refresh
          </b-button>
        </div>
      </div>
    </div>

    <!-- ================= KPIs ================= -->
    <div class="mp-kpis">
      <div class="mp-kpi mp-kpi-paid">
        <div class="mp-kpi-label">TOTAL PAID</div>
        <div class="mp-kpi-value">${{ fmt(totalPaid) }}</div>
        <div class="mp-kpi-sub">{{ filteredPaid.length }} receipt(s)</div>
      </div>
      <div class="mp-kpi mp-kpi-week">
        <div class="mp-kpi-label">THIS WEEK</div>
        <div class="mp-kpi-value">${{ fmt(paidThisWeek) }}</div>
        <div class="mp-kpi-sub">paid in current week</div>
      </div>
      <div class="mp-kpi mp-kpi-month">
        <div class="mp-kpi-label">THIS MONTH</div>
        <div class="mp-kpi-value">${{ fmt(paidThisMonth) }}</div>
        <div class="mp-kpi-sub">paid in current month</div>
      </div>
      <div class="mp-kpi mp-kpi-suspended">
        <div class="mp-kpi-label">SUSPENDED</div>
        <div class="mp-kpi-value">${{ fmt(totalSuspended) }}</div>
        <div class="mp-kpi-sub">{{ filteredSuspended.length }} invoice(s)</div>
      </div>
    </div>

    <b-tabs v-model="innerTab" content-class="mp-inner-content" class="mp-tabs-inner">

      <!-- ========== PAID ========== -->
      <b-tab :title="`Paid (${filteredPaid.length})`">
        <div v-if="loadingPaid" class="mp-loading">
          <b-spinner small></b-spinner> Loading receipts…
        </div>
        <template v-else>
          <div v-if="filteredPaid.length === 0" class="mp-empty">
            <b-icon icon="wallet2" font-scale="2"></b-icon>
            <h4>No payroll receipts in this range</h4>
            <p>Try widening the date range or clearing the location filter.</p>
          </div>
          <div v-else class="mp-table-wrap">
            <table class="mp-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Receipt #</th>
                  <th>Reference #</th>
                  <th v-if="canViewOthers">Salesperson</th>
                  <th>Invoices Paid</th>
                  <th class="text-right">Total</th>
                  <th>Processed By</th>
                  <th class="text-center">Details</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="rec in filteredPaid" :key="rec.id" class="mp-row">
                  <td>{{ rec.payment_date }}</td>
                  <td><strong>{{ rec.payment_receipt }}</strong></td>
                  <td>{{ rec.reference_number }}</td>
                  <td v-if="canViewOthers">{{ rec.salesperson }}</td>
                  <td>
                    <span v-for="(inv, idx) in rec.invoices_json" :key="idx" class="mp-chip">
                      {{ inv.invoice_id || inv.ref }}
                    </span>
                  </td>
                  <td class="text-right text-success font-weight-bold">${{ fmt(rec.total_paid_amount) }}</td>
                  <td>{{ rec.processed_by }}</td>
                  <td class="text-center">
                    <b-button size="sm" class="mp-btn-primary" @click="openReceipt(rec)">
                      <b-icon icon="eye"></b-icon> View
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </b-tab>

      <!-- ========== SUSPENDED ========== -->
      <b-tab :title="`Suspended (${filteredSuspended.length})`">
        <div v-if="loadingSuspended" class="mp-loading">
          <b-spinner small></b-spinner> Loading suspended commissions…
        </div>
        <template v-else>
          <div v-if="filteredSuspended.length === 0" class="mp-empty">
            <b-icon icon="check-circle" font-scale="2"></b-icon>
            <h4>No suspended commissions</h4>
            <p>Everything on your account is currently payable, or the filters are hiding rows.</p>
          </div>
          <div v-else class="mp-table-wrap">
            <table class="mp-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Invoice #</th>
                  <th>Location</th>
                  <th v-if="canViewOthers">Salesperson</th>
                  <th class="text-right">Order Total</th>
                  <th class="text-right">Suspended Comm</th>
                  <th>Reason</th>
                  <th>Suspended By</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in filteredSuspended" :key="row.invoice_id" class="mp-row">
                  <td>{{ formatDate(row.ts) }}</td>
                  <td>
                    <a href="#" class="mp-link" @click.prevent="openInvoice(row.invoice_id)">
                      {{ row.invoice_id }}
                    </a>
                  </td>
                  <td>{{ getLocationName(row.location_id) }}</td>
                  <td v-if="canViewOthers">{{ row.username }}</td>
                  <td class="text-right">${{ fmt(row.order_total) }}</td>
                  <td class="text-right">
                    <strong class="mp-suspended-amt">${{ fmt(suspendedAmount(row)) }}</strong>
                  </td>
                  <td>{{ row.suspend_reason || '—' }}</td>
                  <td>{{ row.suspended_by || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </b-tab>
    </b-tabs>

    <!-- ================= RECEIPT PREVIEW ================= -->
    <b-modal
      id="mp-receipt-modal"
      size="xl"
      hide-footer hide-header centered scrollable
      dialog-class="mp-modal-dialog" content-class="mp-modal-content"
    >
      <div v-if="currentReceipt" class="mp-doc" style="padding: 24px;">
        <button class="mp-close" @click="$bvModal.hide('mp-receipt-modal')">×</button>

        <div class="mp-doc-header">
          <div>
            <div class="mp-doc-label">PAYROLL RECEIPT</div>
            <div class="mp-doc-title">MECCA WHOLESALE LLC</div>
            <div class="mp-doc-sub">101 E Venango Street, Philadelphia, PA 19134</div>
          </div>
          <div class="mp-doc-meta">
            <div><span>Date</span><b>{{ currentReceipt.payment_date }}</b></div>
            <div><span>Receipt #</span><b class="mp-underlined">{{ currentReceipt.payment_receipt }}</b></div>
            <div><span>Reference #</span><b>{{ currentReceipt.reference_number }}</b></div>
          </div>
        </div>

        <div class="mp-group-header">
          <span class="mp-group-name">Paid to: {{ currentReceipt.salesperson }}</span>
          <b class="mp-group-total">${{ fmt(currentReceipt.total_paid_amount) }}</b>
        </div>

        <table class="mp-doc-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Date</th>
              <th>Location</th>
              <th class="text-right">Payable Comm</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(inv, idx) in currentReceipt.invoices_json" :key="idx">
              <td>{{ inv.invoice_id || inv.ref }}</td>
              <td>{{ inv.ts ? formatDate(inv.ts) : '—' }}</td>
              <td>{{ inv.location_id ? getLocationName(inv.location_id) : '—' }}</td>
              <td class="text-right">${{ fmt(inv.payable_commission) }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="currentReceipt.comments && currentReceipt.comments.trim()" class="mt-3">
          <label class="mp-label">Comments</label>
          <div class="mp-comments-box">{{ currentReceipt.comments }}</div>
        </div>

        <div class="mp-grand">
          <span>TOTAL PAID:</span>
          <span>${{ fmt(currentReceipt.total_paid_amount) }}</span>
        </div>

        <div style="text-align: center; margin-top: 16px;">
          <b-button variant="dark" @click="printReceipt">
            <b-icon icon="printer"></b-icon> Print
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Requestor from '@/utilities/Requestor';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';

export default {
  name: 'ReportsMyPayroll',
  components: { DatePicker },
  data() {
    return {
      packet: {},
      loadingPaid: false,
      loadingSuspended: false,

      paidReceipts: [],
      suspendedRows: [],

      // filters
      dateRange: null,
      selectedLocation: null,
      viewAsSalesperson: null,   // null = self

      innerTab: 0,
      currentReceipt: null,
    };
  },
  computed: {
    myUsername() {
      return (this.packet.userDetails && this.packet.userDetails.username) || '';
    },
    canViewOthers() {
      const role = (this.packet.userDetails && this.packet.userDetails.userrole) || '';
      return ['admin', 'locationadmin', 'accountmanager'].includes(role);
    },

    /** The username actually being viewed (self unless overridden by admin) */
    targetUsername() {
      return this.viewAsSalesperson || this.myUsername;
    },

    /** Location dropdown — only locations that appear in this user's data */
    locationOptions() {
      const locIds = new Set();
      for (const rec of this.paidReceipts) {
        for (const inv of rec.invoices_json || []) {
          if (inv.location_id !== undefined && inv.location_id !== null) {
            locIds.add(String(inv.location_id));
          }
        }
      }
      for (const row of this.suspendedRows) {
        if (row.location_id !== undefined && row.location_id !== null) {
          locIds.add(String(row.location_id));
        }
      }
      const locs = this.packet.locations || [];
      return Array.from(locIds)
        .map(id => {
          const found = locs.find(l => String(l.value) === id);
          return { value: id, text: found ? found.text : `Location #${id}` };
        })
        .sort((a, b) => a.text.localeCompare(b.text));
    },

    /** Salesperson dropdown — only shown to admins, populated from packet.users */
    salespersonOptions() {
      const users = this.packet.users || this.packet.salespersons || [];
      return users
        .filter(u => (u.username || u.value) && (u.username || u.value) !== this.myUsername)
        .map(u => ({
          value: u.username || u.value,
          text:  u.fullname || u.name || u.text || u.username || u.value,
        }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },

    /** Date range applied to both lists */
    rangeBounds() {
      if (!this.dateRange || this.dateRange.length !== 2) return null;
      const start = moment(this.dateRange[0], 'MM-DD-YYYY').startOf('day');
      const end   = moment(this.dateRange[1], 'MM-DD-YYYY').endOf('day');
      return { start, end };
    },

    filteredPaid() {
      return this.paidReceipts.filter(rec => {
        // Date
        if (this.rangeBounds) {
          const d = this.parseDate(rec.payment_date);
          if (d && !d.isBetween(this.rangeBounds.start, this.rangeBounds.end, null, '[]')) {
            return false;
          }
        }
        // Location — check any invoice inside the receipt matches
        if (this.selectedLocation) {
          const invoices = rec.invoices_json || [];
          const matches = invoices.some(inv =>
            String(inv.location_id) === String(this.selectedLocation)
          );
          if (!matches) return false;
        }
        return true;
      });
    },

    filteredSuspended() {
      return this.suspendedRows.filter(row => {
        if (this.rangeBounds) {
          const d = this.parseDate(row.ts);
          if (d && !d.isBetween(this.rangeBounds.start, this.rangeBounds.end, null, '[]')) {
            return false;
          }
        }
        if (this.selectedLocation) {
          if (String(row.location_id) !== String(this.selectedLocation)) return false;
        }
        return true;
      });
    },

    totalPaid() {
      return this.filteredPaid.reduce((s, r) => s + Number(r.total_paid_amount || 0), 0);
    },
    paidThisWeek() {
      const start = moment().startOf('isoWeek');
      const end   = moment().endOf('isoWeek');
      return this.filteredPaid.reduce((s, r) => {
        const d = this.parseDate(r.payment_date);
        return d && d.isBetween(start, end, null, '[]')
          ? s + Number(r.total_paid_amount || 0)
          : s;
      }, 0);
    },
    paidThisMonth() {
      const start = moment().startOf('month');
      const end   = moment().endOf('month');
      return this.filteredPaid.reduce((s, r) => {
        const d = this.parseDate(r.payment_date);
        return d && d.isBetween(start, end, null, '[]')
          ? s + Number(r.total_paid_amount || 0)
          : s;
      }, 0);
    },
    totalSuspended() {
      return this.filteredSuspended.reduce((s, r) => s + this.suspendedAmount(r), 0);
    },
  },
  watch: {
    viewAsSalesperson() {
      // Reload from the backend when the admin switches who they're viewing
      this.refreshAll();
    },
  },
  created() {
    this.packet = JSON.parse(localStorage.packet || '{}');
    this.packet.userDetails = JSON.parse(localStorage.userDetails || '{}');
    this.refreshAll();
  },
  methods: {
    fmt(v) { return (parseFloat(v) || 0).toFixed(2); },
    parseDate(str) {
      if (!str) return null;
      const d = moment(str, [
        'MM-DD-YYYY HH:mm:ss A',
        'MM-DD-YYYY HH:mm A',
        'MM-DD-YYYY',
        'YYYY-MM-DD HH:mm:ss',
        'YYYY-MM-DD',
      ], true);
      return d.isValid() ? d : null;
    },
    formatDate(ts) { return ts ? moment(ts).format('MM-DD-YYYY HH:mm A') : '—'; },
    getLocationName(id) {
      if (id === null || id === undefined || id === '') return '—';
      const idStr = String(id).trim();
      if (['0','40','41','42'].includes(idStr)) return 'MECCA WHOLESALE';
      const locs = this.packet.locations || [];
      const found = locs.find(l => String(l.value) === idStr);
      return found ? found.text : `Location #${id}`;
    },
    openInvoice(id) {
      if (!id) return;
      const route = this.$router.resolve({ path: `/sales/viewsale/${id}/` });
      window.open(route.href, '_blank', 'noopener,noreferrer');
    },

    suspendedAmount(row) {
      return (
        Number(row.raw_commission      || 0) +
        Number(row.delivery_commission || 0) +
        Number(row.setup_commission    || 0) +
        Number(row.warranty_commission || 0) +
        Number(row.spiff_commission    || 0)
      );
    },

    /* ---------- filters ---------- */
    setDate(type) {
      const now = moment();
      if (type === 'this_week') {
        this.dateRange = [
          now.clone().startOf('isoWeek').format('MM-DD-YYYY'),
          now.clone().endOf('isoWeek').format('MM-DD-YYYY'),
        ];
      } else if (type === 'last_week') {
        this.dateRange = [
          now.clone().subtract(1, 'week').startOf('isoWeek').format('MM-DD-YYYY'),
          now.clone().subtract(1, 'week').endOf('isoWeek').format('MM-DD-YYYY'),
        ];
      } else if (type === 'this_month') {
        this.dateRange = [
          now.clone().startOf('month').format('MM-DD-YYYY'),
          now.clone().endOf('month').format('MM-DD-YYYY'),
        ];
      } else if (type === 'last_month') {
        this.dateRange = [
          now.clone().subtract(1, 'month').startOf('month').format('MM-DD-YYYY'),
          now.clone().subtract(1, 'month').endOf('month').format('MM-DD-YYYY'),
        ];
      }
    },
    clearDate() { this.dateRange = null; },

    /* ---------- data ---------- */
    async refreshAll() {
      await Promise.all([this.loadPaid(), this.loadSuspended()]);
    },

    async loadPaid() {
      this.loadingPaid = true;
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.getAsyncP('/api/sales/get_my_payroll_receipts/', {
            username: this.myUsername,
            salesperson: this.targetUsername,
          }, this, resolve, reject);
        });
        this.paidReceipts = (res && res.data && res.data.result) || [];
      } catch (e) {
        console.error('[MyPayroll] loadPaid failed:', e);
        this.paidReceipts = [];
      }
      this.loadingPaid = false;
    },

    async loadSuspended() {
      this.loadingSuspended = true;
      try {
        const res = await new Promise((resolve, reject) => {
          Requestor.getAsyncP('/api/sales/get_my_suspended_commissions/', {
            username: this.myUsername,
            salesperson: this.targetUsername,
          }, this, resolve, reject);
        });
        this.suspendedRows = (res && res.data && res.data.result) || [];
      } catch (e) {
        console.error('[MyPayroll] loadSuspended failed:', e);
        this.suspendedRows = [];
      }
      this.loadingSuspended = false;
    },

    openReceipt(rec) {
      this.currentReceipt = rec;
      this.$bvModal.show('mp-receipt-modal');
    },
    printReceipt() { window.print(); },
  },
};
</script>

<style scoped>
/* ============ SHELL ============ */
.mp-page {
  font-family: 'Manrope', system-ui, sans-serif;
  color: #221F1C;
  font-size: 15px;
}

/* ============ HERO ============ */
.mp-hero {
  background: #221F1C; color: #fff;
  border-radius: 16px;
  border-bottom: 4px solid #EE7A22;
  padding: 18px 24px;
  margin-bottom: 14px;
}
.mp-hero-title {
  font-family: 'Sora', sans-serif;
  font-size: 20px; font-weight: 700;
  display: flex; align-items: center; gap: 12px;
}
.mp-hero-sub { font-size: 13px; color: rgba(255,255,255,0.6); margin-top: 4px; }
.mp-count-pill {
  background: #EE7A22; color: #fff;
  font-size: 13px; font-weight: 800;
  padding: 3px 11px; border-radius: 999px;
}

/* ============ FILTERS ============ */
.mp-filterstrip {
  background: #fff;
  border: 1px solid #E5E1DA;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 14px;
}
.mp-label {
  font-size: 11px; font-weight: 800; letter-spacing: 0.08em;
  text-transform: uppercase; color: #736C63;
  margin-bottom: 4px; display: block;
}
:deep(.mx-datepicker) { width: 100%; }
:deep(.mx-input) {
  height: 34px !important;
  font-size: 14px !important;
  border-radius: 8px !important;
  border: 1.5px solid #E5E1DA !important;
  text-align: left;
}

/* ============ KPIs ============ */
.mp-kpis {
  display: grid; grid-template-columns: repeat(4, 1fr);
  gap: 12px; margin-bottom: 14px;
}
@media (max-width: 900px) { .mp-kpis { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 500px) { .mp-kpis { grid-template-columns: 1fr; } }
.mp-kpi {
  background: #fff; border: 1.5px solid #E5E1DA;
  border-radius: 14px; padding: 14px 16px;
}
.mp-kpi-label {
  font-size: 11px; font-weight: 800; letter-spacing: 0.08em;
  color: #736C63; text-transform: uppercase;
}
.mp-kpi-value {
  font-family: 'Sora', sans-serif;
  font-size: 22px; font-weight: 800; margin-top: 4px;
}
.mp-kpi-sub { font-size: 12px; color: #736C63; margin-top: 2px; }
.mp-kpi-paid      { border-color: #2ECC71; background: #F1FCF4; }
.mp-kpi-paid      .mp-kpi-value { color: #1E8449; }
.mp-kpi-week      { border-color: #3498DB; background: #F2F9FF; }
.mp-kpi-week      .mp-kpi-value { color: #1F618D; }
.mp-kpi-month     { border-color: #16A085; background: #F0FBF8; }
.mp-kpi-month     .mp-kpi-value { color: #0E6655; }
.mp-kpi-suspended { border-color: #E74C3C; background: #FFF5F4; }
.mp-kpi-suspended .mp-kpi-value { color: #B03A2E; }

/* ============ TABLE ============ */
.mp-table-wrap { overflow-x: auto; }
.mp-table { width: 100%; border-collapse: separate; border-spacing: 0; }
.mp-table th {
  background: #221F1C; color: #fff;
  font-size: 12px; font-weight: 800;
  text-transform: uppercase; letter-spacing: 0.05em;
  padding: 12px 10px;
  border-bottom: 3px solid #EE7A22;
  text-align: left; white-space: nowrap;
}
.mp-table th.text-right { text-align: right; }
.mp-table th.text-center { text-align: center; }
.mp-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #F1EEE9;
  font-size: 13.5px;
}
.mp-row:hover td { background: #FAF9F7; }

.mp-link { color: #2563eb; font-weight: 700; text-decoration: underline; }
.mp-chip {
  display: inline-block;
  background: #E5E1DA; color: #221F1C;
  padding: 3px 8px; border-radius: 6px;
  font-size: 12px; font-weight: 700;
  margin-right: 4px; margin-bottom: 2px;
}
.mp-suspended-amt { color: #B03A2E; font-size: 15px; }
.mp-btn-primary {
  background: #EE7A22 !important;
  border-color: #EE7A22 !important;
  color: #fff !important;
  font-weight: 700;
}

/* ============ EMPTY / LOADING ============ */
.mp-empty, .mp-loading {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; padding: 60px 20px;
  color: #736C63;
}
.mp-empty h4 {
  font-family: 'Sora', sans-serif;
  font-size: 18px; font-weight: 700;
  color: #221F1C; margin: 14px 0 6px;
}
.mp-empty p { max-width: 44ch; line-height: 1.6; }

/* ============ MODAL / RECEIPT ============ */
.mp-modal-dialog {
  max-width: 1000px !important;
  width: 92% !important;
  margin: 1.75rem auto !important;
}
@media (max-width: 767px) {
  .mp-modal-dialog { width: 100% !important; margin: 0 !important; }
}
.mp-modal-content {
  border: none;
  border-radius: 10px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.35);
}
.mp-doc { position: relative; font-family: 'Manrope', Arial, sans-serif; }
.mp-close {
  position: absolute; top: 12px; right: 16px;
  background: none; border: none; font-size: 26px;
  color: #999; cursor: pointer; z-index: 10;
}
.mp-close:hover { color: #000; }

.mp-doc-header {
  display: flex; justify-content: space-between; gap: 20px;
  border-bottom: 3px solid #EE7A22;
  padding-bottom: 14px; margin-bottom: 20px;
}
.mp-doc-label {
  font-size: 10px; letter-spacing: 0.12em;
  text-transform: uppercase; color: #736C63; font-weight: 700;
}
.mp-doc-title {
  font-family: 'Sora', sans-serif;
  font-size: 20px; font-weight: 800;
  color: #1E1B18; margin-top: 4px;
}
.mp-doc-sub { font-size: 12px; color: #736C63; margin-top: 2px; }
.mp-doc-meta { display: flex; flex-direction: column; gap: 8px; text-align: right; }
.mp-doc-meta span {
  font-size: 10px; text-transform: uppercase;
  letter-spacing: 0.08em; color: #736C63; font-weight: 700; display: block;
}
.mp-doc-meta b { font-size: 14px; color: #221F1C; }
.mp-underlined { text-decoration: underline; }

.mp-group-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 16px; background: #221F1C; color: #fff;
  border-radius: 10px 10px 0 0;
  margin-top: 16px;
}
.mp-group-name { font-family: 'Sora', sans-serif; font-size: 15px; font-weight: 700; }
.mp-group-total { font-family: 'Sora', sans-serif; font-size: 18px; color: #FFB06B; }

.mp-doc-table { width: 100%; border-collapse: collapse; }
.mp-doc-table th {
  background: #F1EEE9; color: #221F1C;
  font-size: 11px; font-weight: 800; text-transform: uppercase;
  text-align: left; padding: 8px 12px;
  border-bottom: 1.5px solid #E5E1DA;
}
.mp-doc-table th.text-right { text-align: right; }
.mp-doc-table td { padding: 8px 12px; border-bottom: 1px solid #F1EEE9; font-size: 13px; }
.mp-doc-table td.text-right { text-align: right; }

.mp-grand {
  display: flex; justify-content: space-between; align-items: center;
  background: #EE7A22; color: #fff;
  padding: 14px 18px; border-radius: 10px;
  font-family: 'Sora', sans-serif; font-size: 16px; font-weight: 800;
  margin-top: 16px;
}
.mp-comments-box {
  background: #FAF9F7; border: 1px solid #E5E1DA;
  border-radius: 8px; padding: 10px 12px;
  font-size: 13px; margin-top: 4px;
}
</style>

<style>
/* Inner tab styling (unscoped) */
.mp-tabs-inner > .nav-tabs {
  padding: 10px 16px 0;
  background: #FAF9F7;
  border-bottom: 1px solid #E5E1DA;
}
.mp-tabs-inner > .nav-tabs .nav-link {
  font-weight: 700;
  color: #736C63;
  border-radius: 8px 8px 0 0;
  border: 1.5px solid transparent;
  padding: 8px 18px;
  margin-bottom: -1px;
}
.mp-tabs-inner > .nav-tabs .nav-link:hover { color: #221F1C; }
.mp-tabs-inner > .nav-tabs .nav-link.active {
  background: #221F1C !important;
  color: #fff !important;
  border-color: #221F1C !important;
}
.mp-tabs-inner > .tab-content { background: #fff; padding: 0; }
</style>


// WEBPACK FOOTER //
// src/components/Reports/ReportsMyPayroll.vue